import type { inferRouterOutputs } from '@trpc/server'

import type { AppRouter } from '@orus.eu/backend'
import { assertDefinedAndNotNull } from '@orus.eu/error'
import { failure, type Result, success } from '@orus.eu/result'
import { localizable, type AnyLocalizableMessage } from '@orus.eu/translations'

import type { StandaloneFormAccess, StandaloneFormRedirect } from '../../standalone-form-access.types.ts'
import type { StandaloneFormPageState, StandaloneFormSubmitProblemType } from '../../standalone-form-entry.types.ts'

type NlAovqlKnowledgeFormOutputs = inferRouterOutputs<AppRouter>['nlAovqlKnowledgeForm']

export type NlAovqlKnowledgeContext = NlAovqlKnowledgeFormOutputs['getCurrentState']

type NlAovqlKnowledgeFormAccess = NlAovqlKnowledgeFormOutputs['getFormAccess']

type NlAovqlKnowledgeSubmitOutput = NlAovqlKnowledgeFormOutputs['submitAttempt']

type NlAovqlKnowledgeSubmitProblemType = Extract<NlAovqlKnowledgeSubmitOutput, { type: 'failure' }>['problem']['type']

export type NlAovqlKnowledgeFormResult = Extract<NlAovqlKnowledgeSubmitOutput, { type: 'success' }>['output']

export function nlAovqlKnowledgePageState(
  context: NlAovqlKnowledgeContext,
): StandaloneFormPageState<NlAovqlKnowledgeFormResult> {
  switch (context.formStatus.state) {
    case 'open':
      return { state: 'open' }
    case 'final':
      switch (context.formStatus.outcome) {
        case 'skipped':
          return { state: 'redirect', redirect: NL_AOVQL_KNOWLEDGE_DENIED_REDIRECT['knowledge-test-skipped'] }
        case 'validated':
        case 'declined':
          return {
            state: 'final',
            result: {
              outcome: context.formStatus.outcome,
              score: assertDefinedAndNotNull(
                context.lastAttempt,
                'While showing the final result of the knowledge form, found no recorded attempt',
                { outcome: context.formStatus.outcome },
              ).score,
              attemptsLeft: context.attemptsLeft,
            },
          }
      }
  }
}

export function nlAovqlKnowledgeSubmitResult(
  output: NlAovqlKnowledgeSubmitOutput,
): Result<NlAovqlKnowledgeFormResult, StandaloneFormSubmitProblemType> {
  switch (output.type) {
    case 'success':
      return success(output.output)
    case 'failure':
      return failure(nlAovqlKnowledgeSubmitProblem(output.problem.type))
  }
}

function nlAovqlKnowledgeSubmitProblem(problem: NlAovqlKnowledgeSubmitProblemType): StandaloneFormSubmitProblemType {
  switch (problem) {
    case 'gate-step-not-reached':
    case 'quote-already-signed':
      return 'form-not-available'
    case 'outdated-definition-version':
    case 'submission-not-allowed':
    case 'invalid-answers':
      return problem
  }
}

export function nlAovqlKnowledgeAccess(access: NlAovqlKnowledgeFormAccess): StandaloneFormAccess {
  switch (access.type) {
    case 'allowed':
      return access
    case 'denied':
      return { type: 'not-available', redirect: NL_AOVQL_KNOWLEDGE_DENIED_REDIRECT[access.reason] }
  }
}

const NL_AOVQL_KNOWLEDGE_DENIED_REDIRECT: Record<
  Extract<NlAovqlKnowledgeFormAccess, { type: 'denied' }>['reason'],
  StandaloneFormRedirect
> = {
  // The funnel moves a customer on from its quote step to the step they are at
  'gate-step-not-reached': { to: 'funnel', stepId: 'quote' },
  'knowledge-test-skipped': { to: 'funnel', stepId: 'quote' },
  'quote-already-signed': { to: 'customer-portal' },
}

export function nlAovqlKnowledgeAttemptsLeftMessage(attemptCount: number): AnyLocalizableMessage {
  return attemptCount === 1
    ? 'nl_aovql_knowledge_test_one_attempt_left'
    : localizable({ key: 'nl_aovql_knowledge_test_attempts_left', parameters: { attemptCount } })
}
